import { useDisclosure } from '@chakra-ui/react';
import { CollaborationAccessLevelEnum } from '@monetize/types/app';
import { useState } from 'react';
import { Outlet } from 'react-router';
import { MBox, MPageLoader } from '../../../components/Monetize';
import { ROLES } from '../../../constants';
import {
  QUOTE_EDIT_ACTIONABLE_STATUSES,
  QUOTE_READONLY_STATUSES,
} from '../../../constants/quotes';
import { useACL } from '../../../services/acl/acl';
import { useAuth } from '../../../services/auth0';
import { QuoteContactsDrawerProvider } from '../Quote/components/contacts/quoteContactsDrawerContext';
import { ManualQuoteAcceptanceModal } from '../Quote/ManualQuoteAcceptanceModal';
import { useQuoteContext } from '../Quote/quoteContext';
import { QuotePageCustomer } from './components/QuotePageCustomer';
import { QuotePageHeader } from './components/QuotePageHeader';
import { QuotePageInternal } from './components/QuotePageInternal';

export const QuotePage = () => {
  const { canDo } = useACL();
  const [cacheBustTimestamp, setCacheBustTimestamp] = useState(() =>
    new Date().getTime(),
  );
  const [headerHeight, setHeaderHeight] = useState(0);
  const { quoteData, isInternalView, loadData } = useQuoteContext();
  const manualAcceptanceModalState = useDisclosure();
  const {
    onOpen: onOpenManualAcceptanceModal,
    onClose: onCloseManualAcceptanceModal,
    open: isOpenManualAcceptanceModal,
  } = manualAcceptanceModalState;
  const { quote, displayConfig } = quoteData;

  const { currentTenantUserHasRole } = useAuth();
  const hasValidRole = currentTenantUserHasRole(ROLES.QUOTES_ROLES);

  if (!quote || !displayConfig) {
    return <MPageLoader />;
  }

  const canUpdateSales = canDo([['sales', 'update']]);
  const hasEditAccess =
    canUpdateSales || quote.accessLevel === CollaborationAccessLevelEnum.READ;

  const canEditQuote =
    hasEditAccess &&
    QUOTE_EDIT_ACTIONABLE_STATUSES.has(quote.status) &&
    hasValidRole;

  const customizePdfFormDisabled =
    QUOTE_READONLY_STATUSES.has(quote.status) ||
    !hasEditAccess ||
    !hasValidRole;

  return (
    <QuoteContactsDrawerProvider>
      <QuotePageHeader
        canEditQuote={canEditQuote}
        hasValidRole={hasValidRole}
        onOpenManualAcceptanceModal={onOpenManualAcceptanceModal}
        onHeaderHeightChange={setHeaderHeight}
      />

      <MBox width="auto" px={['4px', '4px', '32px']} pb="20">
        <MBox margin="0 auto">
          {isInternalView && (
            <QuotePageInternal
              key={cacheBustTimestamp}
              customizePdfFormDisabled={customizePdfFormDisabled}
              headerHeight={headerHeight}
            />
          )}
          {!isInternalView && (
            <QuotePageCustomer
              key={cacheBustTimestamp}
              showDownloadButton
              showQuoteInformation
              isExternalView={!isInternalView}
              customizePdfFormDisabled={customizePdfFormDisabled}
              onForceQuoteRefresh={() =>
                setCacheBustTimestamp(new Date().getTime())
              }
              headerHeight={headerHeight}
            />
          )}
        </MBox>
      </MBox>

      <ManualQuoteAcceptanceModal
        isOpen={isOpenManualAcceptanceModal}
        quoteId={quote.id}
        onClose={() => {
          onCloseManualAcceptanceModal();
          loadData();
        }}
      />
      <Outlet />
    </QuoteContactsDrawerProvider>
  );
};
